// src/admin/subpagesList.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/** Build auth headers that work for both API key and JWT */
function getAuthHeaders(token) {
  const t = token?.toString().trim();
  return t
    ? {
        Authorization: `Bearer ${t}`,
        "X-API-Key": t,
      }
    : {};
}

/* ------------------------- PageSelect (dropdown) ------------------------- */
function PageSelect({ baseURL, token, value, onChange }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const http = useMemo(
    () =>
      axios.create({
        baseURL,
        headers: {
          ...getAuthHeaders(token),
        },
      }),
    [baseURL, token]
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await http.get(`/pages`, {
          params: { status: "active", sort: "page_name", order: "asc" },
        });
        if (!mounted) return;
        setPages(data?.items || []);
      } catch (e) {
        if (!mounted) return;
        setErr(e?.response?.data?.message || e.message || "Failed to load pages");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [http]);

  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      className="w-full border rounded px-3 py-2 mt-1"
    >
      <option value="">{loading ? "Loading pages…" : "-- Select Page --"}</option>
      {err && <option disabled>{err}</option>}
      {pages.map((p) => (
        <option key={p.page_id} value={p.page_id}>
          {p.page_name}
        </option>
      ))}
    </select>
  );
}

/* ------------------------- Subpages List + CRUD ------------------------- */
export default function SubpagesList({
  baseURL = "https://gantasoft.in/rankridgecollege/dev/v1",
  token = "12345678", // string is safer for headers
  pageId = null, // optional filter by main page
}) {
  const api = useMemo(() => {
    return axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(token),
      },
    });
  }, [baseURL, token]);

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const params = {
        
        
        
      };
      if (pageId) params.page_id = pageId;

      const { data } = await api.get("/subpages", { params });
      setRows(data?.items || []);
      setTotal(data?.pagination?.total || 0);
      setPageCount(data?.pagination?.pages || 0);
    } catch (e) {
      setError(e?.response?.data?.debug || e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, page, limit, pageId]);

  function openAdd() {
    setEditing({
      subpage_id: null,
      page_id: pageId || null,
      subpage_name: "",
      subpage_slug: "",
      status: "active",
    });
  }
  function openEdit(it) {
    setEditing({ ...it });
  }

  // auto-slug on name change (only if user hasn't typed slug)
  function onNameChange(val) {
    setEditing((s) => {
      if (!s) return s;
      const next = { ...s, subpage_name: val };
      if (!s.subpage_slug?.trim()) {
        next.subpage_slug = val
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")
          .trim()
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-");
      }
      return next;
    });
  }

  async function save() {
    if (!editing) return;
    const payload = {
      page_id: editing.page_id,
      subpage_name: editing.subpage_name?.trim(),
      subpage_slug: editing.subpage_slug?.trim(),
      status: editing.status || "active",
    };
    if (!payload.page_id) return alert("Please select a Page");
    if (!payload.subpage_name || !payload.subpage_slug) return alert("Name & Slug required");

    setSaving(true);
    try {
      if (editing.subpage_id) {
        await api.put(`/subpages/${editing.subpage_id}`, payload, {
          headers: { "Content-Type": "application/json" },
        });
      } else {
        await api.post(`/subpages`, payload, {
          headers: { "Content-Type": "application/json" },
        });
      }
      setEditing(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function destroy(it) {
    if (!window.confirm("Delete this subpage?")) return;
    try {
      await api.delete(`/subpages/${it.subpage_id}`);
      setRows((r) => r.filter((x) => x.subpage_id !== it.subpage_id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Delete failed");
    }
  }

  async function toggleStatus(it) {
    const next = it.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/subpages/${it.subpage_id}`, { field: "status", value: next });
      setRows((arr) =>
        arr.map((x) => (x.subpage_id === it.subpage_id ? { ...x, status: next } : x))
      );
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Update failed");
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-4">
      <div className="flex items-end justify-between gap-2 flex-wrap">
        <h2 className="text-lg font-semibold">Subpages</h2>
        <button onClick={openAdd} className="px-3 py-1.5 bg-green-700 text-white rounded">
          + Add Subpage
        </button>
      </div>

      <div className="mt-3 grid sm:grid-cols-4 gap-2">
        <input
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
          placeholder="Search name/slug"
          className="border rounded px-3 py-2"
        />
        <select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          className="border rounded px-3 py-2"
        >
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>
        <select
          value={limit}
          onChange={(e) => {
            setPage(1);
            setLimit(Number(e.target.value));
          }}
          className="border rounded px-3 py-2"
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}/page
            </option>
          ))}
        </select>
        <button onClick={load} className="px-3 py-2 bg-gray-800 text-white rounded">
          Refresh
        </button>
      </div>

      {error && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="mt-4 text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-4 text-gray-500">No results.</div>
      ) : (
        <div className="mt-4 grid gap-3">
          {rows.map((it) => (
            <div key={it.subpage_id} className="border rounded p-3 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{it.subpage_name}</div>
                  <div className="text-xs text-gray-600">
                    ID: {it.subpage_id} · Page: {it.page_id}
                  </div>
                  <div className="text-xs text-gray-600">
                    Slug: <code className="bg-gray-100 px-1 rounded">{it.subpage_slug}</code>
                  </div>
                </div>
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{it.status}</span>
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => openEdit(it)} className="px-2 py-1 text-xs rounded bg-blue-600 text-white">
                  Edit
                </button>
                <button onClick={() => toggleStatus(it)} className="px-2 py-1 text-xs rounded bg-gray-800 text-white">
                  {it.status === "active" ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => destroy(it)} className="px-2 py-1 text-xs rounded bg-red-600 text-white">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pageCount > 1 && (
        <div className="mt-4 flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 rounded bg-gray-200 disabled:opacity-50"
          >
            Prev
          </button>
          <div className="text-sm">
            Page {page} of {pageCount} · {total} total
          </div>
          <button
            disabled={page >= pageCount}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded bg-gray-200 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Modal Editor */}
      {editing && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setEditing(null)}
        >
          <div className="w-full max-w-md bg-white rounded-2xl p-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold">{editing.subpage_id ? "Edit Subpage" : "Add Subpage"}</h3>
            <div className="grid gap-3 max-h-[60vh] overflow-y-auto mt-2">
              <label className="text-sm">
                Page
                <PageSelect
                  baseURL={baseURL}
                  token={token}
                  value={editing.page_id}
                  onChange={(val) => setEditing((s) => ({ ...s, page_id: val }))}
                />
              </label>
              <label className="text-sm">
                Name
                <input
                  value={editing.subpage_name || ""}
                  onChange={(e) => onNameChange(e.target.value)}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </label>
              <label className="text-sm">
                Slug
                <input
                  value={editing.subpage_slug || ""}
                  onChange={(e) => setEditing((s) => ({ ...s, subpage_slug: e.target.value }))}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </label>
              <label className="text-sm">
                Status
                <select
                  value={editing.status || "active"}
                  onChange={(e) => setEditing((s) => ({ ...s, status: e.target.value }))}
                  className="w-full border rounded px-3 py-2 mt-1"
                >
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded bg-green-700 text-white">
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 rounded bg-gray-200">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
